import React from 'react'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { Button } from '../ui/button'
import { leavePageTo } from '@/lib/player/leave'

interface ErrorFallbackProps {
  /** Whatever was thrown: not always an `Error`. */
  error: unknown
  componentStack: string | null
  eventId: string | null
  resetError: () => void
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetError, eventId }) => {
  const { t, i18n } = useTranslation('common')
  const [isReportSent, setIsReportSent] = React.useState(false)

  const handleReportFeedback = () => {
    if (eventId) {
      Sentry.showReportDialog({
        eventId,
        lang: i18n.language,
        title: t('errorBoundary.reportTitle'),
        subtitle: t('errorBoundary.reportSubtitle'),
        subtitle2: t('errorBoundary.reportSubtitle2'),
        labelName: t('errorBoundary.labelName'),
        labelEmail: t('errorBoundary.labelEmail'),
        labelComments: t('errorBoundary.labelComments'),
        labelClose: t('errorBoundary.labelClose'),
        labelSubmit: t('errorBoundary.labelSubmit'),
        errorGeneric: t('errorBoundary.errorGeneric'),
        errorFormEntry: t('errorBoundary.errorFormEntry'),
        successMessage: t('errorBoundary.successMessage'),
        /* Hide the button only once the form really opened; a blocked or failed dialog leaves it there to try again. */
        onLoad: () => setIsReportSent(true),
      })
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-4 text-center">
          {t('errorBoundary.title')}
        </h1>

        <p className="text-gray-600 mb-6 text-center">
          {t('errorBoundary.description')}
        </p>

        {import.meta.env.DEV && (
          <div className="mb-6 p-4 bg-gray-100 rounded-lg">
            <h3 className="font-semibold text-sm text-gray-700 mb-2">
              {t('errorBoundary.errorDetails')}:
            </h3>
            <pre className="text-xs text-red-600 whitespace-pre-wrap break-words">
              {error instanceof Error ? error.message : String(error)}
            </pre>
            {eventId && (
              <p className="text-xs text-gray-500 mt-2">
                Event ID: {eventId}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={resetError}
            variant="default"
            className="bg-primary hover:bg-primary/90"
          >
            {t('errorBoundary.tryAgain')}
          </Button>

          {eventId && !isReportSent && (
            <Button
              onClick={handleReportFeedback}
              variant="outline"
            >
              {t('errorBoundary.reportFeedback')}
            </Button>
          )}

          <Button
            onClick={() => leavePageTo('/')}
            variant="outline"
          >
            {t('errorBoundary.goHome')}
          </Button>
        </div>
      </div>
    </div>
  )
}

interface ErrorBoundaryProps {
  children: React.ReactNode
}

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack, eventId, resetError }) => (
        <ErrorFallback
          error={error}
          componentStack={componentStack}
          eventId={eventId}
          resetError={resetError}
        />
      )}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
